/** The asset page's Processing tab: each ingest run and its steps, and retrying a failed one. */
import { useNavigate } from 'react-router-dom';
import { Check, CircleDashed, Loader2, Minus, RotateCcw, X } from 'lucide-react';
import type { AssetDetail, ProcessingRunDTO } from '@prism/contracts';
import { errorMessage } from '../api/client';
import { useAssetAction, useMe } from '../api/hooks';
import { StatusDot } from '../components/Status';
import { toast } from '../components/Toasts';
import { elapsed, relative } from '../lib/format';
import { encoderText } from '../lib/encoder';
import { DetectShotsPanel } from '../components/DetectShotsDialog';
import { RegenerateProxyPanel } from '../components/RegenerateProxy';

export function RetryBar({ asset }: { asset: AssetDetail }) {
  const me = useMe().data!;
  const retry = useAssetAction('retry');
  const dismiss = useAssetAction('dismiss');
  const navigate = useNavigate();
  if (!me.permissions.includes('processing.retry')) return null;
  return (
    <div className="actions">
      <button className="btn sm" data-testid="retry-button" disabled={retry.isPending}
        onClick={() => retry.mutate(asset.id, { onSuccess: () => toast('Processing restarted'), onError: (e) => toast(errorMessage(e), 'error') })}>
        <RotateCcw size={13} /> Retry processing
      </button>
      {!asset.dismissed && (
        <button className="btn ghost sm" data-testid="dismiss-button"
          onClick={() => dismiss.mutate(asset.id, { onSuccess: () => { toast('Dismissed from the library'); navigate('/library'); } })}>
          Dismiss
        </button>
      )}
    </div>
  );
}

export function Steps({ run, compact = false }: { run: ProcessingRunDTO; compact?: boolean }) {
  return (
    <div className="steps">
      {run.steps.map((s) => {
        const pct = s.detail.percent as number | undefined;
        return (
          <div className="step" key={s.step} data-step={s.step} data-status={s.status}>
            <span className={`step-icon ${s.status}`}>
              {s.status === 'succeeded' ? <Check size={12} strokeWidth={3} /> : s.status === 'running' ? <Loader2 size={12} className="spin" /> : s.status === 'failed' ? <X size={12} strokeWidth={3} /> : s.status === 'skipped' ? <Minus size={12} /> : <CircleDashed size={12} />}
            </span>
            <div className="step-main">
              <span className="step-title">{s.label}</span>
              {!compact && s.error && <span className="step-sub" style={{ color: 'var(--danger)' }}>{s.error.message}</span>}
              {!compact && s.status === 'skipped' && typeof s.detail.reason === 'string' && <span className="step-sub">{s.detail.reason}</span>}
              {s.status === 'running' && pct != null && <div className="progress" style={{ width: 160 }}><span style={{ width: `${pct}%` }} /></div>}
              {!compact && s.attempts > 1 && <span className="step-sub">{s.attempts} attempts</span>}
              {!compact && s.status === 'succeeded' && encoderText(s.detail.encoder, s.detail.decoder) && <span className="step-sub" data-testid="step-encoder">{encoderText(s.detail.encoder, s.detail.decoder)}</span>}
              {s.status === 'running' && typeof s.detail.waitingFor === 'string' && <span className="step-sub" style={{ color: 'var(--warning)' }}>Waiting for {s.detail.waitingFor.replace(/^Storage agent/, 'storage agent')}</span>}
            </div>
            <span className="step-time">{s.status === 'running' && pct != null ? `${Math.round(pct)}%` : elapsed(s.startedAt, s.finishedAt)}</span>
          </div>
        );
      })}
    </div>
  );
}

export function ProcessingTab({ asset }: { asset: AssetDetail }) {
  if (asset.status === 'placeholder') return <p className="muted" data-testid="processing-tab">No media yet, so nothing has been processed.</p>;
  return (
    <div data-testid="processing-tab">
      {(asset.status === 'failed' || asset.status === 'partial') && asset.error && (
        <div className={`banner ${asset.status === 'failed' ? 'error' : 'warning'}`} style={{ marginBottom: 14 }} role="alert">
          <div className="banner-body">
            <strong>{asset.status === 'failed' ? 'Processing failed' : 'Partially processed'}</strong>
            <span>{asset.error.message}</span>
            {asset.error.hint && <span className="muted">{asset.error.hint}</span>}
            <RetryBar asset={asset} />
          </div>
        </div>
      )}
      {/* 1.5.0 (S2): the shots line and Detect shots…. */}
      <DetectShotsPanel asset={asset} />
      {/* 1.6.1: Regenerate proxy (administrators). */}
      <RegenerateProxyPanel asset={asset} />
      {asset.runs.length === 0 && <p className="muted">No processing has run yet.</p>}
      {asset.runs.map((run) => (
        <div className="run" key={run.id} data-testid="processing-run">
          <div className="run-head">
            <strong>{run.kind === 'shots' ? 'Detect shots' : `Ingest · attempt ${run.attempt}`}</strong>
            <StatusDot tone={{ queued: 'registered', running: 'processing', succeeded: 'ready', partial: 'partial', failed: 'failed', cancelled: 'offline' }[run.status]}>
              {{ queued: 'Queued', running: 'Running', succeeded: 'Succeeded', partial: 'Partial', failed: 'Failed', cancelled: 'Cancelled' }[run.status]}
            </StatusDot>
            <span style={{ flex: 1 }} />
            <span className="faint" style={{ fontSize: 12 }}>{relative(run.createdAt)}{run.requestedBy ? ` · ${run.requestedBy}` : ''}</span>
          </div>
          <Steps run={run} />
          <div className="faint mono" style={{ fontSize: 11 }} title="Use these to find the run in Temporal and in service logs">
            workflow {run.workflowId} · correlation {run.correlationId.slice(0, 8)}{run.finishedAt ? ` · ${elapsed(run.startedAt ?? run.createdAt, run.finishedAt)}` : ''}
          </div>
        </div>
      ))}
    </div>
  );
}
